/*
 * Copyright 2020 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * External dependencies
 */
import { produce } from 'immer';

/**
 * Internal dependencies
 */
import {
  ensureGroupLayersAreAdjacent,
  getAbsolutePosition,
  moveArrayElement,
} from './utils';
import type { ReducerState, ReducerStateDraft } from './types';

enum LayerDirection {
  Front = 'FRONT',
  Back = 'BACK',
  Forward = 'FORWARD',
  Backward = 'BACKWARD',
}

type ArrangeElementProps = {
  elementId?: string | null;
  position: number | LayerDirection;
  groupId?: string | false | null;
};

/**
 * Move element in element order on the current page (optionally handle group id when moved in the Layers Panel).
 *
 * If no element is given, use the current selection. Single selection reorders the element,
 * multi-selection reorders the selected elements as a contiguous block.
 *
 * If the element does not exist on the current page, state is unchanged.
 *
 * If the element is background element, state is unchanged (you can't reorder the background).
 *
 * Element is given by id and must be moved to the given position.
 * The position can either be a number from 0 to the number of elements, or a string
 * equal to one of the constants: FRONT, BACK, FORWARD, BACKWARD.
 *
 * FRONT is the same as moving to the highest possible number. BACK is the same as moving to 0.
 *
 * FORWARD and BACKWARD is changing the elements relative position by plus or minus 1 respectively.
 *
 * If there is a current background element, both BACK and position 0 is treated as position 1.
 *
 * If element is already at the desired position, state is unchanged.
 *
 * If group is set to a specific id, that id is enforced and will be copied to group.
 * If group is set to null or undefined, group is required to not be present and will be removed.
 * If group is unset or set to false, group will be ignored whether set or not and will not be changed or removed.
 *
 * Selection and current page is unchanged.
 *
 */
export const arrangeElement = (
  draft: ReducerStateDraft,
  { elementId, position, groupId = false }: ArrangeElementProps
) => {
  const page = draft.pages.find(({ id }) => id === draft.current);

  if (!page) {
    return;
  }

  let idToArrange = elementId;

  if (idToArrange === null) {
    const { selection } = draft;

    if (selection.length === 0) {
      return;
    }

    if (selection.length > 1) {
      const backgroundId = page.elements[0]?.id;
      const selectedIds = new Set(selection);
      if (backgroundId) {
        selectedIds.delete(backgroundId);
      }

      const selectedElements: typeof page.elements = [];
      const selectedIndices: number[] = [];
      const remainingElements: typeof page.elements = [];

      page.elements.forEach((element, index) => {
        if (selectedIds.has(element.id)) {
          selectedElements.push(element);
          selectedIndices.push(index);
        } else {
          remainingElements.push(element);
        }
      });

      if (selectedElements.length === 1) {
        idToArrange = selectedElements[0].id;
      } else if (selectedElements.length > 1) {
        // Abort if there's less than three elements (nothing to rearrange as first is bg)
        if (page.elements.length < 3) {
          return;
        }

        if (groupId && !page.groups?.[groupId]) {
          return;
        }

        const minPosition = 1;
        const maxPosition = remainingElements.length;
        let insertPosition: number;

        if (typeof position === 'number') {
          const clampedPosition = Math.min(
            page.elements.length,
            Math.max(minPosition, position)
          );
          const removedBefore = selectedIndices.filter(
            (index) => index < clampedPosition
          ).length;
          insertPosition = clampedPosition - removedBefore;
        } else {
          const blockPosition = Math.min(
            maxPosition,
            Math.max(minPosition, selectedIndices[0])
          );
          insertPosition = getAbsolutePosition({
            currentPosition: blockPosition,
            minPosition,
            maxPosition,
            desiredPosition: position,
          });
        }

        insertPosition = Math.min(
          maxPosition,
          Math.max(minPosition, insertPosition)
        );

        const previousGroupIds = new Set(
          selectedElements
            .map((element) => element.groupId)
            .filter(Boolean)
        );

        if (groupId) {
          selectedElements.forEach((element) => {
            element.groupId = groupId;
          });
        } else if (groupId === null) {
          selectedElements.forEach((element) => {
            delete element.groupId;
          });
        }

        const newElements = [
          ...remainingElements.slice(0, insertPosition),
          ...selectedElements,
          ...remainingElements.slice(insertPosition),
        ];

        page.elements = ensureGroupLayersAreAdjacent(newElements);

        if (groupId !== false && page.groups) {
          previousGroupIds.forEach((previousGroupId) => {
            if (previousGroupId === groupId) {
              return;
            }
            const groupHasElements = page.elements.some(
              (element) => element.groupId === previousGroupId
            );
            if (!groupHasElements) {
              delete page.groups[previousGroupId];
            }
          });
        }

        return;
      } else {
        return;
      }
    } else {
      idToArrange = selection[0];
    }
  }

  if (!idToArrange) {
    return;
  }

  // Abort if there's less than three elements (nothing to rearrange as first is bg)
  if (page.elements.length < 3) {
    return;
  }

  const currentPosition = page.elements.findIndex(
    ({ id }) => id === idToArrange
  );

  if (currentPosition === -1 || page.elements[0].id === idToArrange) {
    return;
  }

  const minPosition = 1;
  const maxPosition = page.elements.length - 1;
  const newPosition = getAbsolutePosition({
    currentPosition,
    minPosition,
    maxPosition,
    desiredPosition: position,
  });

  const currentGroupId = page.elements[currentPosition].groupId;

  // If it's already there, do nothing.
  if (currentPosition === newPosition && currentGroupId === groupId) {
    return;
  }

  // Update group id on current element
  if (groupId) {
    // Can only change groups to a group that exists
    if (!page.groups?.[groupId]) {
      return;
    }
    page.elements[currentPosition].groupId = groupId;
  } else if (groupId === undefined || groupId === null) {
    delete page.elements[currentPosition].groupId;
  }

  // If there are no elements left in the group, remove the group as well.
  if (currentGroupId) {
    const groupHasElements = page.elements.some(
      (e) => e.groupId === currentGroupId
    );
    if (!groupHasElements && page.groups) {
      delete page.groups[currentGroupId];
    }
  }

  // Then reorder if relevant
  page.elements = moveArrayElement(page.elements, currentPosition, newPosition);

  page.elements = ensureGroupLayersAreAdjacent(page.elements);
};

// @ts-ignore
export default produce<ReducerState, [ArrangeElementProps]>(arrangeElement);
